import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card, EmptyState, Field } from "../components/ui-ext";
import { MapPin, Clock, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "../components/ExportButton";

export default function AttendancePage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [today, setToday] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/attendance").then((r) => {
    setItems(r.data);
    const t = new Date().toISOString().slice(0, 10);
    setToday(r.data.find((a) => a.date === t && a.engineer_id === user.id));
  });

  useEffect(() => { load(); }, []); // eslint-disable-line

  const checkIn = async () => {
    setBusy(true);
    try {
      const coords = await getGPS();
      const payload = {
        date: new Date().toISOString().slice(0, 10),
        check_in_time: new Date().toISOString(),
        latitude: coords?.latitude,
        longitude: coords?.longitude,
        location_name: coords ? `${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}` : null,
      };
      await api.post("/attendance/check-in", payload);
      toast.success("Checked in with GPS");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Check-in failed");
    } finally { setBusy(false); }
  };

  const checkOut = async (id) => {
    setBusy(true);
    try {
      const coords = await getGPS();
      await api.post(`/attendance/${id}/check-out`, {
        check_out_time: new Date().toISOString(),
        latitude: coords?.latitude,
        longitude: coords?.longitude,
      });
      toast.success("Checked out");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Check-out failed");
    } finally { setBusy(false); }
  };

  const isEng = user.role === "engineer";

  return (
    <div>
      <PageHeader
        eyebrow="Attendance"
        title={isEng ? "My Attendance" : "Team Attendance"}
        subtitle={isEng ? "Daily check-in with GPS location." : "Attendance log across the team."}
        right={!isEng && <ExportButton endpoint="attendance.xlsx" testid="export-attendance-btn" />}
      />

      {isEng && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-6 md:col-span-2">
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#FF5722] mb-4">Today · {new Date().toDateString()}</div>
            {today ? (
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="w-3 h-3 rounded-full bg-green-500 pulse-dot" />
                  <div>
                    <div className="text-lg font-semibold">Checked in</div>
                    <div className="mono text-xs text-slate-400">
                      {new Date(today.check_in_time).toLocaleTimeString()} · {today.location_name || "No location"}
                    </div>
                  </div>
                </div>
                {today.check_out_time ? (
                  <div className="flex items-center gap-4 pt-2 border-t border-white/10">
                    <div className="w-3 h-3 rounded-full bg-slate-500" />
                    <div>
                      <div className="text-sm font-medium">Checked out</div>
                      <div className="mono text-xs text-slate-400">{new Date(today.check_out_time).toLocaleTimeString()}</div>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => checkOut(today.id)} disabled={busy} className="border border-white/15 hover:border-[#FF5722] px-4 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="check-out-btn">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    <Clock className="w-4 h-4" /> Check Out
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div className="text-lg font-semibold">Ready to check in</div>
                  <div className="text-sm text-slate-400 mt-1">Enable location for accurate attendance tracking.</div>
                </div>
                <button onClick={checkIn} disabled={busy} className="bg-[#FF5722] hover:bg-[#F4511E] text-white px-6 py-3 text-sm rounded-sm flex items-center gap-2 disabled:opacity-50" data-testid="check-in-btn">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                  <MapPin className="w-4 h-4" /> Check In (GPS)
                </button>
              </div>
            )}
          </Card>
          <Card className="p-6">
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">This Month</div>
            <div className="mono text-4xl font-medium">{items.filter((a) => a.date?.startsWith(new Date().toISOString().slice(0, 7))).length}</div>
            <div className="text-xs text-slate-500 mt-2">days present</div>
          </Card>
        </div>
      )}

      <Card>
        {items.length === 0 ? (
          <EmptyState title="No attendance yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full tech-table">
              <thead>
                <tr>
                  <th>Date</th>
                  {!isEng && <th>Engineer</th>}
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Location</th>
                  <th>GPS</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} data-testid={`att-row-${a.id}`}>
                    <td className="mono">{a.date}</td>
                    {!isEng && <td>{a.engineer_name}</td>}
                    <td className="mono text-xs">{a.check_in_time ? new Date(a.check_in_time).toLocaleTimeString() : "—"}</td>
                    <td className="mono text-xs">{a.check_out_time ? new Date(a.check_out_time).toLocaleTimeString() : "—"}</td>
                    <td className="text-xs">{a.location_name || "—"}</td>
                    <td className="mono text-xs text-slate-500">{a.latitude ? `${a.latitude.toFixed(3)}, ${a.longitude.toFixed(3)}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function getGPS() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 8000, enableHighAccuracy: true }
    );
  });
}
